import { Component, ElementRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { CheckboxModule } from 'primeng/checkbox';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { StoreStockComponent } from '../parts/store-stock/store-stock.component';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import { StorePresetDialogComponent } from '../store-preset-dialog/store-preset-dialog.component';
import { StorePreset } from '../../services/store-preset.service';
import { BicProduct, BicStock } from 'src/app/models/biccamera-product';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import {
  BiccameraProductsGQL,
  DeleteBiccameraProductGQL,
  RegsiterQueueBiccameraGQL,
  UpdateUserBiccameraProductGQL,
} from 'src/gql/generated';
import { LoggingGQL } from '../../../gql/generated';
import moment from 'moment';

@Component({
  selector: 'app-biccamera',
  templateUrl: './biccamera.component.html',
  styleUrls: ['./biccamera.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    CardModule,
    DialogModule,
    MultiSelectModule,
    PanelModule,
    CheckboxModule,
    Textarea,
    TooltipModule,
    StoreStockComponent,
    KeepaGraphComponent,
    StorePresetDialogComponent,
  ],
})
export class BiccameraComponent implements OnInit {
  searchText: string;
  form: UntypedFormGroup;
  isLoading: boolean;
  products: Array<BicProduct> = [];
  showZeroStock = false;
  showAnotherPriceModal = false;
  anotherPriceModel = {
    bicProduct: null,
    price: null,
  };

  canRegisterNewItems = true;

  finishedBuildStores = false;
  selectedArea = [];
  groupedArea = [];
  selectedPref = [];
  groupedPref = [];
  selectedStores = [];
  groupedStores = [];

  queueItems = [];

  splitStr = '::';
  selectedAreaKey = 'savedSelectedAreaKey';
  selectedPrefKey = 'savedSelectedPrefKey';
  selectedStoreKey = 'savedSelectedStoreKey';

  constructor(
    private elementRef: ElementRef,
    private message: MessageService,
    private subtitleService: SubtitleService,
    private routingService: RoutingService,
    private userService: UserService,
    private createGql: RegsiterQueueBiccameraGQL,
    private getGql: BiccameraProductsGQL,
    private userBicUpdateGql: UpdateUserBiccameraProductGQL,
    private deleteBicProductGQL: DeleteBiccameraProductGQL,
    private loggingGql: LoggingGQL,
  ) {
    this.isLoading = false;
    this.routingService.loginRequired();
    this.userService.biccameraRequired();
    this.subtitleService.setSubTitle('ビックカメラ');
  }

  get filteredProducts(): Array<BicProduct> {
    return this.products.filter((product) => {
      if (!product.hasAnyStock && !this.showZeroStock) {
        return false;
      }
      const hasFilteredStock = product.filteredStocks.filter((s) => s.hasStock).length > 0;
      if (!hasFilteredStock && !this.showZeroStock) {
        return false;
      }
      return true;
    });
  }

  get filteredProductsCount(): number {
    return this.filteredProducts.length;
  }

  async ngOnInit() {
    this.selectedArea = JSON.parse(localStorage.getItem(this.selectedAreaKey)) || [];
    this.selectedPref = JSON.parse(localStorage.getItem(this.selectedPrefKey)) || [];
    this.selectedStores = JSON.parse(localStorage.getItem(this.selectedStoreKey)) || [];
    await this.loadProducts();
  }

  async loadProducts() {
    const resp = await firstValueFrom(this.getGql.fetch());

    this.canRegisterNewItems = resp.data.assertBiccameraProducts.canRegisterNewItem;

    this.queueItems = resp.data.queueBiccameraProducts;
    this.products = [];
    resp.data.biccameraProducts.forEach((p) => {
      if (p.isDeleted) {
        return;
      }
      const hokkaido: BicStock[] = [];
      const kinki: BicStock[] = [];
      const tyugoku: BicStock[] = [];
      const kyuushu: BicStock[] = [];
      const kantou: BicStock[] = [];
      const tyubu: BicStock[] = [];

      // Extract Amazon product information
      const sp = p.product;
      const mainAp = sp.amazonProducts?.find((ap) => ap.isMain);
      const ap = mainAp ?? (sp.amazonProducts?.length > 0 ? sp.amazonProducts[0] : null);

      const product: BicProduct = {
        id: p.id,
        itemId: p.biccameraItemId,
        jan: sp.jan,
        price: p.price,
        name: ap?.name || p.productName,
        lastCheckedAt: p.lastCheckedAt,
        createdAt: p.createdAt,
        hasAnyStock: false,
        hokkaido,
        kinki,
        tyugoku,
        kyuushu,
        kantou,
        tyubu,
        all: [],
        totalStockNum: p.totalStockNum,
        userBicId: p.userBiccameraProduct.id,
        userName: p.userBiccameraProduct.user?.name,
        anotherPrice: p.userBiccameraProduct.anotherPrice,
        // Amazon product information
        asin: ap?.asin || '',
        amazonPrice: ap?.latestPrice || -1,
        imageUrl: ap?.imageUrl || '',
        boughtPastMonth: ap?.keepaProduct?.boughtPastMonth,
      };

      p.stocks.forEach((s) => {
        if (s.hasStock) {
          product.hasAnyStock = true;
        }
        const stock: BicStock = {
          prefecture: s.prefecture,
          area: s.areaName,
          storeName: s.storeName,
          hasStock: s.hasStock,
          stockMark: s.stockMark,
          note: s.note,
        };
        if (s.areaName === '関東') {
          kantou.push(stock);
        }
        if (s.areaName === '中部') {
          tyubu.push(stock);
        }
        if (s.areaName === '中国') {
          tyugoku.push(stock);
        }
        if (s.areaName === '近畿') {
          kinki.push(stock);
        }
        if (s.areaName === '九州') {
          kyuushu.push(stock);
        }
        if (s.areaName === '北海道') {
          hokkaido.push(stock);
        }
      });

      const prefSort = (s1, s2) => (s1.prefecture > s2.prefecture ? 1 : -1);
      kantou.sort(prefSort);
      tyubu.sort(prefSort);
      tyugoku.sort(prefSort);
      kinki.sort(prefSort);
      kyuushu.sort(prefSort);
      hokkaido.sort(prefSort);

      product.all = [...kantou, ...tyubu, ...tyugoku, ...kinki, ...kyuushu, ...hokkaido];
      product.filteredStocks = this.doFilters(product.all);

      this.buildSelectOpts(kantou, tyubu, tyugoku, kinki, kyuushu, hokkaido);

      this.products.push(product);
    });
    this.logging();
  }

  buildSelectOpts(
    kantou: BicStock[],
    tyubu: BicStock[],
    tyugoku: BicStock[],
    kinki: BicStock[],
    kyuushu: BicStock[],
    hokkaido: BicStock[],
  ) {
    if (this.finishedBuildStores) {
      return;
    }
    // 何かしらデータが存在する場合に利用する
    if (kantou.length === 0) {
      return;
    }

    // TODO: area と pref もbuildする
    this.buildArea();
    this.buildPref(kantou, tyubu, tyugoku, kinki, kyuushu, hokkaido);
    this.buildGroupedStores(kantou, tyubu, tyugoku, kinki, kyuushu, hokkaido);

    this.finishedBuildStores = true;
  }

  buildArea() {
    this.groupedArea = [
      {
        label: 'エリア',
        value: 'area',
        items: [
          {
            label: '関東',
            value: '関東',
          },
          {
            label: '中部',
            value: '中部',
          },
          {
            label: '中国',
            value: '中国',
          },
          {
            label: '近畿',
            value: '近畿',
          },
          {
            label: '九州',
            value: '九州',
          },
          {
            label: '北海道',
            value: '北海道',
          },
        ],
      },
    ];
  }

  buildPref(
    kantou: BicStock[],
    tyubu: BicStock[],
    tyugoku: BicStock[],
    kinki: BicStock[],
    kyuushu: BicStock[],
    hokkaido: BicStock[],
  ) {
    this.groupedPref = [
      {
        label: '関東',
        value: '関東',
        items: [
          ...new Set(
            kantou.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
      {
        label: '中部',
        value: '中部',
        items: [
          ...new Set(
            tyubu.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
      {
        label: '中国',
        value: '中国',
        items: [
          ...new Set(
            tyugoku.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
      {
        label: '近畿',
        value: '近畿',
        items: [
          ...new Set(
            kinki.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
      {
        label: '九州',
        value: '九州',
        items: [
          ...new Set(
            kyuushu.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
      {
        label: '北海道',
        value: '北海道',
        items: [
          ...new Set(
            hokkaido.map((s) => {
              return s.prefecture;
            }),
          ),
        ].map((p) => {
          return {
            label: p,
            value: p,
          };
        }),
      },
    ];
  }

  buildGroupedStores(
    kantou: BicStock[],
    tyubu: BicStock[],
    tyugoku: BicStock[],
    kinki: BicStock[],
    kyuushu: BicStock[],
    hokkaido: BicStock[],
  ) {
    this.groupedStores = [
      {
        label: '関東',
        value: '関東',
        items: kantou.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
      {
        label: '中部',
        value: '中部',
        items: tyubu.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
      {
        label: '中国',
        value: '中国',
        items: tyugoku.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
      {
        label: '近畿',
        value: '近畿',
        items: kinki.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
      {
        label: '九州',
        value: '九州',
        items: kyuushu.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
      {
        label: '北海道',
        value: '北海道',
        items: hokkaido.map((s) => {
          return {
            label: s.prefecture + ' ' + s.storeName,
            value: s.prefecture + this.splitStr + s.storeName,
          };
        }),
      },
    ];
  }

  canRegister(): boolean {
    return !!this.searchText && !this.isLoading && this.canRegisterNewItems;
  }

  getStatus(st: boolean): string {
    return st ? '◯' : '×';
  }

  getMyStocks(stocks: BicStock[]): string {
    return stocks
      .map((s) => {
        return s.storeName;
      })
      .join('<br/>');
  }

  getRowStyle(product: BicProduct) {
    if (!product.hasAnyStock) {
      return 'unpublic';
    }
    const isZetoStock = product.filteredStocks.filter((s) => s.hasStock).length === 0;
    if (isZetoStock) {
      return 'unpublic';
    }
    return '';
  }

  async doRegister() {
    if (!this.canRegister()) {
      // なぜかdisabledでも押せることがある
      return;
    }
    const searchText = this.searchText;
    if (!searchText) {
      this.message.setFlashMessageWithClear('warn', '入力してください', '');
      return;
    }
    // trim each line, filter empty lines, and remove duplicates
    const texts = [
      ...new Set(
        searchText
          .split(/\n/)
          .map((t) => t.trim())
          .filter((t) => t),
      ),
    ];

    const ngTexts = [];
    this.isLoading = true;
    texts.forEach(async (txt) => {
      const splitted = txt.split(/\//, -1);
      let id: string;
      if (splitted.length > 1) {
        id = splitted[splitted.length - 1].trim();
        if (!id) {
          id = splitted[splitted.length - 2].trim();
        }
      } else {
        id = txt.trim();
      }
      if (Number.isNaN(Number(id))) {
        ngTexts.push(txt);
      } else {
        await firstValueFrom(this.createGql.mutate({ variables: { itemId: id } }));
      }
    });

    await this.loadProducts();
    if (ngTexts.length > 0) {
      this.message.setFlashMessageWithClear('warn', '登録できなかった商品があります', ngTexts.join('\n'));
    } else {
      this.message.setFlashMessageWithClear('success', '登録しました', '');
    }
    this.searchText = '';
    this.isLoading = false;
  }

  doFilters(stocks: BicStock[]) {
    stocks = this.doFilterStocks(stocks);
    stocks = this.doFilterArea(stocks);
    stocks = this.doFilterPref(stocks);
    stocks = this.doFilterStores(stocks);
    return stocks;
  }

  private doFilterStocks(stocks: BicStock[]) {
    if (stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => s.hasStock);
  }

  private doFilterArea(stocks: BicStock[]) {
    if (this.selectedArea.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedArea.includes(s.area);
    });
  }

  private doFilterPref(stocks: BicStock[]) {
    if (this.selectedPref.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedPref.includes(s.prefecture);
    });
  }

  private doFilterStores(stocks: BicStock[]) {
    if (this.selectedStores.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedStores.includes(s.prefecture + this.splitStr + s.storeName);
    });
  }

  doChangeArea($e) {
    localStorage.setItem(this.selectedAreaKey, JSON.stringify(this.selectedArea));
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });

    this.logging();
  }

  doChangePref($e) {
    localStorage.setItem(this.selectedPrefKey, JSON.stringify(this.selectedPref));
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });

    this.logging();
  }

  doChangeStores($e) {
    localStorage.setItem(this.selectedStoreKey, JSON.stringify(this.selectedStores));
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });

    this.logging();
  }

  clearArea() {
    this.selectedArea = [];
    this.doChangeArea(null);
  }

  clearPref() {
    this.selectedPref = [];
    this.doChangePref(null);
  }

  clearStores() {
    this.selectedStores = [];
    this.doChangeStores(null);
  }

  onPresetLoaded(preset: StorePreset) {
    this.selectedArea = preset.selectedArea || [];
    this.selectedPref = preset.selectedPref || [];
    this.selectedStores = preset.selectedStores || [];
    this.doChangeArea(null);
    this.doChangePref(null);
    this.doChangeStores(null);
  }

  downloadCsv(table: any) {
    const products: BicProduct[] = table.filteredValue ?? this.filteredProducts;
    const values = products.filter((p) => p.filteredStocks.length > 0);
    const csv = values
      .map((p) => {
        const stores = p.filteredStocks.map((s) => s.storeName + ':' + s.stockMark).join('/');
        const date = moment(p.createdAt).format('MMDD');
        const totalStock = p.totalStockNum != null ? p.totalStockNum : '-';
        const boughtPastMonth = p.boughtPastMonth != null ? p.boughtPastMonth : '-';
        const memo = `${date}_総在${totalStock}_先販${boughtPastMonth}_${stores}`;
        return p.jan + ',' + p.price + ',1,,"' + (memo ?? '') + '"';
      })
      .join('\n');
    this.outputCsv(csv, 'biccamera.csv');
  }

  openAnotherPrice(bicProduct: BicProduct) {
    this.anotherPriceModel.bicProduct = bicProduct;
    this.showAnotherPriceModal = true;
  }

  async doRegisterAnotherPrice() {
    const id = this.anotherPriceModel.bicProduct.userBicId;
    const anotherPrice = this.anotherPriceModel.price;
    const result = await firstValueFrom(this.userBicUpdateGql.mutate({ variables: { id, anotherPrice } }));

    if (result.data.updateUserBiccameraProduct) {
      this.anotherPriceModel.bicProduct.anotherPrice = anotherPrice;
    }

    this.showAnotherPriceModal = false;
    this.anotherPriceModel = {
      bicProduct: null,
      price: null,
    };
  }

  async doDelete(product: BicProduct) {
    const result = await this.deleteBicProductGQL.mutate({ variables: { id: product.userBicId } }).toPromise();
    if (result.data?.deleteBiccameraProduct) {
      this.message.setFlashMessageWithClear('success', '削除成功', '削除しました', 3000);
      await this.loadProducts();
    } else {
      this.message.setFlashMessageWithClear(
        'error',
        'エラー',
        '予期せぬエラーが発生しました。もう一度やり直してください。',
        3000,
      );
    }
  }

  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-donwload') as HTMLAnchorElement;
    link.href = url;
    link.download = filename;
    link.click();
  }

  private async logging() {
    this.userService.me;
    await firstValueFrom(
      this.loggingGql.fetch({
        variables: {
          name: 'bigcamera',
          log: JSON.stringify([this.userService.getName(), this.selectedArea, this.selectedPref, this.selectedStores]),
        },
      }),
    );
  }
}
